// Real HTTP adapter for the Home Dashboard (FEATURE_010). Talks to admin-api under /admin/dashboard:
// `stats` = the 5 summary cards, `charts` = the 2 time-series charts. Selected over the in-memory mock
// when VITE_USE_MOCK_API=false (see dashboard.adapter.ts). Maps the server error envelope
// { error:{ code, message } } to DashboardApiError; a missing response (offline) becomes code 'NETWORK'.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  DashboardApiError,
  type DashboardApi,
  type DashboardApiErrorCode,
  type DashboardChartBundle,
  type DashboardPeriod,
  type DashboardStats,
} from '../types/dashboard.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: { code?: string; message?: string };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  // No response object → request never reached the server (offline / DNS / CORS).
  if (!candidate.response) {
    throw new DashboardApiError('NETWORK', '네트워크 연결을 확인해주세요.');
  }
  const payload = candidate.response.data?.error;
  throw new DashboardApiError(
    (payload?.code as DashboardApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
  );
}

export const dashboardHttpApi: DashboardApi = {
  async getSummary(): Promise<DashboardStats> {
    try {
      const res = await httpClient.get<ApiEnvelope<DashboardStats>>(
        endpoints.dashboard.stats,
      );
      const data = res.data?.data;
      if (!data || typeof data !== 'object') {
        throw new DashboardApiError('UNKNOWN', '데이터를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      if (e instanceof DashboardApiError) throw e;
      mapError(e);
    }
  },

  async getCharts(period: DashboardPeriod): Promise<DashboardChartBundle> {
    try {
      const res = await httpClient.get<ApiEnvelope<DashboardChartBundle>>(
        endpoints.dashboard.charts,
        { params: { period } },
      );
      const data = res.data?.data;
      if (!data || typeof data !== 'object') {
        throw new DashboardApiError('UNKNOWN', '차트 데이터를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      if (e instanceof DashboardApiError) throw e;
      mapError(e);
    }
  },
};
